import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../config';
import {
    Users, UserCheck, Clock, AlertCircle, TrendingUp, Calendar,
    FileText, DollarSign, ChevronRight, BarChart2, RefreshCw,
    Bell, Star, Zap, Activity, CheckCircle, LogOut, Briefcase, Bookmark, Mail
} from 'lucide-react';
import './Dashboard.css';
import EmployeeGrowthChart from './Charts/EmployeeGrowthChart';
import AttendancePieChart from './Charts/AttendancePieChart';
import DepartmentChart from './Charts/DepartmentChart';

const API_BASE = API_URL;
console.log("DEBUG: Current API_BASE is:", API_BASE);
// External Swagger API for Departments
const DEPT_API = process.env.REACT_APP_DEPT_API_URL;
const DEPT_TOKEN = process.env.REACT_APP_DEPT_API_TOKEN;

const parseHolidayDate = (dateStr) => {
    if (!dateStr) return new Date();
    const parts = dateStr.split('-');
    if (parts.length === 3 && parts[2].length === 4) {
        return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    }
    return new Date(dateStr);
};

const Dashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [userName, setUserName] = useState('User');
    const [userRole, setUserRole] = useState('EMPLOYEE');
    const [viewMode, setViewMode] = useState('personal'); // 'org' or 'personal'

    // Management Stats
    const [stats, setStats] = useState([]);
    const [recentLeaves, setRecentLeaves] = useState([]);
    const [onboardingList, setOnboardingList] = useState([]);
    const [deptData, setDeptData] = useState([]);
    const [externalDepts, setExternalDepts] = useState([]);
    const [growthData, setGrowthData] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [chartMode, setChartMode] = useState('growth');
    const [detailedStats, setDetailedStats] = useState({ retention: 0, diversity: 0, tenure: 0 });
    const [deptError, setDeptError] = useState(null);
    const [gpsEnabled, setGpsEnabled] = useState(true);
    const [togglingGps, setTogglingGps] = useState(false);
    const [generatingReport, setGeneratingReport] = useState(false);

    // Personal Stats
    const [personalStats, setPersonalStats] = useState({
        attendanceStatus: 'Not Punched',
        pendingLeaves: 0,
        upcomingHoliday: null,
        leaveBalance: 0
    });
    const [myRequests, setMyRequests] = useState([]);

    // Daily Attendance Summary
    const [dailySummaryDate, setDailySummaryDate] = useState(new Date().toISOString().split('T')[0]);
    const [dailySummary, setDailySummary] = useState({ summary: {}, employees: [] });

    // Notices/Announcements
    const [notices, setNotices] = useState([]);
    const [showNoticeModal, setShowNoticeModal] = useState(false);
    const [noticeForm, setNoticeForm] = useState({ id: null, title: '', message: '', type: 'info', priority: 0 });
    const [allNotices, setAllNotices] = useState([]);

    useEffect(() => {
        const initializeDashboard = async () => {
            const storedUser = localStorage.getItem('zTaskUserDetails');
            let initialViewMode = 'personal';
            let initialRole = 'EMPLOYEE';

            if (storedUser) {
                try {
                    const user = JSON.parse(storedUser);
                    setUserName(user.first_name || 'User');
                    initialRole = (user.role?.name || user.claims?.roles || 'EMPLOYEE').toUpperCase();
                    setUserRole(initialRole);

                    if (['SUPER_ADMIN', 'ADMIN', 'HR'].includes(initialRole)) {
                        initialViewMode = 'org';
                    }
                } catch (e) {
                    console.error("Error parsing user details", e);
                }
            }

            setViewMode(initialViewMode);
            // We pass the parameters directly to ensure we use the correct initial values
            const storedToken = localStorage.getItem('zTaskAuthToken');
            const headers = { Authorization: `Bearer ${storedToken}` };
            const userId = JSON.parse(storedUser || '{}').id || JSON.parse(storedUser || '{}').user_id;

            await fetchData(initialViewMode, initialRole, headers, userId);
        };

        initializeDashboard();
    }, []);

    const fetchData = async (mode, role, headers, userId) => {
        setRefreshing(true);
        try {
            // Always fetch notices for everyone
            await fetchActiveNotices();

            if (mode === 'org' && ['SUPER_ADMIN', 'ADMIN', 'HR'].includes(role)) {
                await fetchOrgData(headers);
                await fetchAllNotices(headers); // Admin can see all notices
            } else {
                await fetchPersonalData(headers, userId);
            }
        } catch (error) {
            console.error("Dashboard Fetch Error:", error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const fetchDashboardData = async () => {
        const token = localStorage.getItem('zTaskAuthToken');
        const headers = { Authorization: `Bearer ${token}` };
        const storedUser = JSON.parse(localStorage.getItem('zTaskUserDetails') || '{}');
        const userId = storedUser.id || storedUser.user_id || storedUser.claims?.user_ID;

        await fetchData(viewMode, userRole, headers, userId);
    };

    const fetchOrgData = async (headers) => {
        try {
            const deptToken = localStorage.getItem('REACT_APP_DEPT_API_TOKEN') || headers.Authorization?.split(' ')[1];
            const deptHeaders = {
                'accept': 'application/json',
                'Authorization': `Bearer ${deptToken}`
            };

            const [empRes, leaveRes, deptRes, holidayRes, gpsRes] = await Promise.all([
                axios.get(`${API_BASE}/hr/employees?limit=1000`, { headers }).catch(e => ({ data: { data: [] } })),
                axios.get(`${API_BASE}/leaves/requests`, { headers }).catch(e => ({ data: [] })),
                axios.get(DEPT_API, { headers: deptHeaders }).catch(e => ({ data: { departments: [] }, error: e.message })),
                axios.get(`${API_BASE}/holidays`, { headers }).catch(e => ({ data: [] })),
                axios.get(`${API_BASE}/gps-config`, { headers }).catch(e => {
                    console.error("GPS Config Fetch Failed, defaulting to OFF:", e);
                    return { data: { gpsEnabled: false } };
                })
            ]);

            setGpsEnabled(gpsRes.data.gpsEnabled);

            // Fetch Daily Attendance Summary
            await fetchDailySummary(headers, dailySummaryDate);

            if (deptRes.error) {
                setDeptError(deptRes.error);
            } else {
                setDeptError(null);
            }

            const employees = Array.isArray(empRes.data.data) ? empRes.data.data : (Array.isArray(empRes.data) ? empRes.data : []);
            const leaves = Array.isArray(leaveRes.data) ? leaveRes.data : (Array.isArray(leaveRes) ? leaveRes : []);

            const deptResData = deptRes.data || {};
            const externalDeptsData = Array.isArray(deptResData.departments) ? deptResData.departments
                : (Array.isArray(deptResData.data) ? deptResData.data
                    : (Array.isArray(deptResData.message) ? deptResData.message
                        : (Array.isArray(deptResData) ? deptResData : [])));

            const holidays = Array.isArray(holidayRes.data) ? holidayRes.data : (Array.isArray(holidayRes) ? holidayRes : []);

            // Org Logic - Be more inclusive with status checks
            const activeEmployees = employees.filter(e => {
                const status = (e.status_name || e.employee_status || '').toUpperCase();
                return ['ACTIVE', 'ONBOARDED', 'PROBATION', 'ONBOARDING'].includes(status) || !e.status_name;
            });

            const onboardingCount = employees.filter(e => {
                const status = (e.status_name || e.employee_status || '').toLowerCase();
                return status.includes('probation') || status.includes('onboarding') ||
                    (e.created_at && new Date(e.created_at) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
            }).length;

            const pendingLeaves = leaves.filter(l => (l.status || '').toLowerCase() === 'pending');

            const today = new Date().setHours(0, 0, 0, 0);
            const nextHoliday = holidays
                .filter(h => parseHolidayDate(h.date).getTime() >= today)
                .sort((a, b) => parseHolidayDate(a.date) - parseHolidayDate(b.date))[0];

            setStats([
                { name: 'Total Workforce', value: activeEmployees.length || employees.length, change: employees.length + ' Total', changeType: 'increase', icon: Users, color: '#3b82f6', bg: 'var(--blue-subtle)', path: '/employees' },
                { name: 'Active Onboarding', value: onboardingCount, change: 'Live', changeType: 'neutral', icon: UserCheck, color: '#10b981', bg: 'var(--green-subtle)', path: '/onboarding' },
                { name: 'Pending Approvals', value: pendingLeaves.length, change: pendingLeaves.length > 0 ? 'Urgent' : 'Clear', changeType: pendingLeaves.length > 0 ? 'decrease' : 'neutral', icon: Clock, color: '#f59e0b', bg: 'var(--orange-subtle)', path: '/leaves' },
                { name: 'Holidays', value: holidays.length, change: nextHoliday ? nextHoliday.name : 'Scheduled', changeType: 'neutral', icon: Calendar, color: '#af52de', bg: '#fef2ff', path: '/holidays' }
            ]);

            setExternalDepts(externalDeptsData);
            setRecentLeaves(pendingLeaves.slice(0, 5));
            setOnboardingList([...employees].reverse().slice(0, 5));

            // Detailed Stats
            const totalCount = employees.length || 1; // Avoid division by zero
            const retentionRate = Math.round(((activeEmployees.length || employees.length) / totalCount) * 100);

            const genders = employees.map(e => (e.gender || e.sex_name || '').toLowerCase()).filter(Boolean);
            const mCount = genders.filter(g => g === 'male' || g === 'm').length;
            const fCount = genders.filter(g => g === 'female' || g === 'f').length;
            const diversityIndex = totalCount > 1 ? (1 - (Math.pow(mCount / totalCount, 2) + Math.pow(fCount / totalCount, 2))).toFixed(2) : "1.00";

            const avgTenure = employees.length > 0 ? (employees.reduce((acc, e) => {
                const joinDate = e.created_at ? new Date(e.created_at).getTime() : Date.now();
                return acc + (Date.now() - joinDate);
            }, 0) / totalCount / (1000 * 60 * 60 * 24 * 365)).toFixed(1) : "0.0";

            setDetailedStats({ retention: retentionRate, diversity: diversityIndex, tenure: avgTenure });

            // Dept Map
            const deptMap = {};
            if (externalDeptsData.length > 0) {
                externalDeptsData.forEach(d => { deptMap[d.departmentName] = 0; });
            }
            employees.forEach(e => {
                const d = e.department_name || e.dept_name || 'General';
                deptMap[d] = (deptMap[d] || 0) + 1;
            });
            setDeptData(Object.keys(deptMap).map(k => ({ department: k, count: deptMap[k] })));

            setGrowthData(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'].map((m, i) => ({
                month: m,
                count: Math.round((activeEmployees.length || 10) * (0.8 + (i * 0.04)))
            })));

            setAttendanceData([
                { label: 'Present', value: 85, color: '#10b981' },
                { label: 'Absent', value: 5, color: '#ef4444' },
                { label: 'On Leave', value: 7, color: '#f59e0b' },
                { label: 'WFH', value: 3, color: '#3b82f6' }
            ]);
        } catch (error) {
            console.error("Org Data Error:", error);
            throw error;
        }
    };

    const fetchDailySummary = async (headers, date) => {
        try {
            const res = await axios.get(`${API_BASE}/attendance/daily-summary?date=${date}`, { headers });
            setDailySummary(res.data || { summary: {}, employees: [] });
        } catch (e) {
            console.error("Daily Summary Error:", e);
            setDailySummary({ summary: {}, employees: [] });
        }
    };

    const handleDailySummaryDateChange = async (newDate) => {
        setDailySummaryDate(newDate);
        const token = localStorage.getItem('zTaskAuthToken');
        const headers = { Authorization: `Bearer ${token}` };
        await fetchDailySummary(headers, newDate);
    };

    // Notices Functions
    const fetchActiveNotices = async () => {
        try {
            const res = await axios.get(`${API_BASE}/notices/active`);
            setNotices(res.data || []);
        } catch (e) {
            console.error("Notices Error:", e);
        }
    };

    const fetchAllNotices = async (headers) => {
        try {
            const res = await axios.get(`${API_BASE}/notices`, { headers });
            setAllNotices(res.data || []);
        } catch (e) {
            console.error("All Notices Error:", e);
        }
    };

    const saveNotice = async () => {
        const token = localStorage.getItem('zTaskAuthToken');
        const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
        try {
            if (noticeForm.id) {
                await axios.put(`${API_BASE}/notices/${noticeForm.id}`, noticeForm, { headers });
            } else {
                await axios.post(`${API_BASE}/notices`, noticeForm, { headers });
            }
            setShowNoticeModal(false);
            setNoticeForm({ id: null, title: '', message: '', type: 'info', priority: 0 });
            fetchActiveNotices();
            fetchAllNotices(headers);
        } catch (e) {
            console.error("Save Notice Error:", e);
            alert("Failed to save notice");
        }
    };

    const deleteNotice = async (id) => {
        if (!window.confirm("Delete this notice?")) return;
        const token = localStorage.getItem('zTaskAuthToken');
        const headers = { Authorization: `Bearer ${token}` };
        try {
            await axios.delete(`${API_BASE}/notices/${id}`, { headers });
            fetchActiveNotices();
            fetchAllNotices(headers);
        } catch (e) {
            console.error("Delete Notice Error:", e);
        }
    };

    const toggleNotice = async (id) => {
        const token = localStorage.getItem('zTaskAuthToken');
        const headers = { Authorization: `Bearer ${token}` };
        try {
            await axios.patch(`${API_BASE}/notices/${id}/toggle`, {}, { headers });
            fetchActiveNotices();
            fetchAllNotices(headers);
        } catch (e) {
            console.error("Toggle Notice Error:", e);
        }
    };

    const fetchPersonalData = async (headers, userId) => {
        try {
            const [leaveRes, leaveSummaryRes, holidayRes, attenRes] = await Promise.all([
                axios.get(`${API_BASE}/leaves/requests`, { headers }).catch(e => ({ data: [] })),
                axios.get(`${API_BASE}/leaves/summary`, { headers }).catch(e => ({ data: { stats: { remaining: 0 } } })),
                axios.get(`${API_BASE}/holidays`, { headers }).catch(e => ({ data: [] })),
                axios.get(`${API_BASE}/attendance/stats?userId=${userId}`, { headers }).catch(e => ({ data: { present_days: 0 } }))
            ]);

            const requests = Array.isArray(leaveRes.data) ? leaveRes.data : [];
            const myLeaves = requests.filter(l => l.user_id === userId || !l.user_id);
            const pendingCount = myLeaves.filter(l => (l.status || '').toLowerCase() === 'pending').length;
            const leaveBal = leaveSummaryRes.data?.stats?.remaining || leaveSummaryRes.data?.remaining || 0;

            const holidays = Array.isArray(holidayRes.data) ? holidayRes.data : [];
            const nextHoliday = holidays
                .filter(h => parseHolidayDate(h.date) > new Date())
                .sort((a, b) => parseHolidayDate(a.date) - parseHolidayDate(b.date))[0];

            setPersonalStats({
                attendanceStatus: (attenRes.data?.present_days > 0 || attenRes.data > 0) ? 'Active' : 'Neutral',
                pendingLeaves: pendingCount,
                upcomingHoliday: nextHoliday ? `${nextHoliday.name} (${parseHolidayDate(nextHoliday.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })})` : 'None',
                leaveBalance: leaveBal
            });

            setMyRequests(myLeaves.slice(0, 5));
            setGrowthData(['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => ({
                month: d,
                count: Math.floor(Math.random() * 5) + 5
            })));
        } catch (error) {
            console.error("Personal Data Error:", error);
            throw error;
        }
    };

    const toggleViewMode = () => {
        const newMode = viewMode === 'org' ? 'personal' : 'org';
        setViewMode(newMode);
    };

    // Re-fetch when viewMode changes
    useEffect(() => {
        if (!loading) {
            fetchDashboardData();
        }
    }, [viewMode]);

    if (loading) return (
        <div className="dashboard-loading fade-in">
            <div className="loader-container">
                <div className="loader-ring"></div>
                <Users className="loader-logo" size={32} />
            </div>
            <p className="loading-text">Personalizing your workspace...</p>
        </div>
    );

    const toggleGpsLogic = async () => {
        setTogglingGps(true);
        try {
            const token = localStorage.getItem('zTaskAuthToken');
            const headers = { Authorization: `Bearer ${token}` };
            const res = await axios.post(`${API_BASE}/gps-config/toggle`, { enabled: !gpsEnabled }, { headers });
            setGpsEnabled(res.data.gpsEnabled);
        } catch (error) {
            console.error("Error toggling GPS logic:", error);
            alert(error.response?.data?.error || "Failed to toggle GPS logic");
        } finally {
            setTogglingGps(false);
        }
    };

    const handleGenerateReport = async () => {
        setGeneratingReport(true);
        try {
            const token = localStorage.getItem('zTaskAuthToken');
            const headers = { Authorization: `Bearer ${token}` };
            await axios.post(`${API_BASE}/trigger-daily-report`, {}, { headers });
            alert('✅ Daily HR Report has been sent to all Admin/HR emails!');
        } catch (error) {
            console.error("Error generating report:", error);
            alert(error.response?.data?.error || "Failed to generate report. Please try again.");
        } finally {
            setGeneratingReport(false);
        }
    };

    const isManagement = ['SUPER_ADMIN', 'ADMIN', 'HR'].includes(userRole);
    const canToggleGPS = ['SUPER_ADMIN', 'ADMIN'].includes(userRole);

    return (
        <div className="dashboard-container-modern fade-in">
            {/* Header */}
            <header className="dashboard-header">
                <div className="greeting-section">
                    <span className="welcome-tag">{userRole.replace('_', ' ')} SESSION</span>
                    <h1>Hello, {userName} 👋</h1>
                    <p>{viewMode === 'org' ? "Organization Analytics Overview" : "Your personal productivity dashboard"}</p>
                </div>
                <div className="header-actions">
                    {canToggleGPS && viewMode === 'org' && (
                        <div className="gps-toggle-container">
                            <span className="gps-toggle-label">GPS Tracking:</span>
                            <button
                                className={`btn-gps-toggle ${gpsEnabled ? 'enabled' : 'disabled'}`}
                                onClick={toggleGpsLogic}
                                disabled={togglingGps}
                            >
                                {togglingGps ? <RefreshCw size={14} className="spin" /> : (gpsEnabled ? <Zap size={14} /> : <Zap size={14} style={{ opacity: 0.5 }} />)}
                                {gpsEnabled ? 'On' : 'Off'}
                            </button>
                        </div>
                    )}
                    {isManagement && viewMode === 'org' && (
                        <button
                            className="btn-secondary"
                            onClick={handleGenerateReport}
                            disabled={generatingReport}
                            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none' }}
                        >
                            <Mail size={18} />
                            {generatingReport ? 'Sending...' : 'Email Report'}
                        </button>
                    )}
                    {isManagement && (
                        <button className="btn-secondary" onClick={toggleViewMode}>
                            {viewMode === 'org' ? <Bookmark size={18} /> : <BarChart2 size={18} />}
                            {viewMode === 'org' ? 'My Dashboard' : 'Org View'}
                        </button>
                    )}
                    <button className={`btn-primary ${refreshing ? 'disabled' : ''}`} onClick={fetchDashboardData}>
                        <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
                        {refreshing ? 'Syncing...' : 'Sync Feed'}
                    </button>
                </div>
            </header>

            {/* Dynamic Notices Banner */}
            {notices.length > 0 ? (
                <div className="notices-carousel" style={{ marginBottom: '16px' }}>
                    {notices.slice(0, 3).map((notice, idx) => (
                        <div key={notice.id} className="activity-banner" style={{
                            marginBottom: idx < notices.length - 1 ? '8px' : '0',
                            background: notice.type === 'urgent' ? '#fef2f2' : notice.type === 'warning' ? '#fffbeb' : notice.type === 'success' ? '#f0fdf4' : '#eff6ff',
                            borderLeft: `4px solid ${notice.type === 'urgent' ? '#ef4444' : notice.type === 'warning' ? '#f59e0b' : notice.type === 'success' ? '#10b981' : '#3b82f6'}`
                        }}>
                            {notice.type === 'urgent' ? <AlertCircle size={16} color="#ef4444" /> :
                                notice.type === 'warning' ? <Zap size={16} color="#f59e0b" /> :
                                    notice.type === 'success' ? <CheckCircle size={16} color="#10b981" /> :
                                        <Bell size={16} color="#3b82f6" />}
                            <span style={{ color: notice.type === 'urgent' ? '#991b1b' : notice.type === 'warning' ? '#92400e' : notice.type === 'success' ? '#166534' : '#1e40af' }}>
                                {notice.title && <strong>{notice.title}: </strong>}{notice.message}
                            </span>
                            {notice.author_first_name && (
                                <span style={{ marginLeft: 'auto', fontSize: '10px', color: '#94a3b8' }}>— {notice.author_first_name}</span>
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="activity-banner">
                    <Zap size={16} color="#f59e0b" />
                    <span><strong>Welcome!</strong> {viewMode === 'org' ? "Manage your organization from this dashboard." : "Track your attendance and leave from here."}</span>
                </div>
            )}

            {/* Notice Management for Admin/HR */}
            {isManagement && viewMode === 'org' && (
                <div className="info-card-premium" style={{ padding: '16px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Bell size={18} color="#3b82f6" />
                            <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>📢 Manage Notices & Announcements</h4>
                        </div>
                        <button
                            onClick={() => { setNoticeForm({ id: null, title: '', message: '', type: 'info', priority: 0 }); setShowNoticeModal(true); }}
                            style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            + Add Notice
                        </button>
                    </div>
                    <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                        {allNotices.length > 0 ? allNotices.map(n => (
                            <div key={n.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', borderBottom: '1px solid #f1f5f9', fontSize: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                    <span style={{
                                        width: '8px', height: '8px', borderRadius: '50%',
                                        background: n.is_active ? '#10b981' : '#94a3b8'
                                    }}></span>
                                    <span style={{
                                        padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: '600',
                                        background: n.type === 'urgent' ? '#fee2e2' : n.type === 'warning' ? '#fef3c7' : n.type === 'success' ? '#dcfce7' : '#dbeafe',
                                        color: n.type === 'urgent' ? '#dc2626' : n.type === 'warning' ? '#d97706' : n.type === 'success' ? '#16a34a' : '#2563eb'
                                    }}>{n.type}</span>
                                    <span style={{ fontWeight: '500' }}>{n.title || n.message.substring(0, 40)}</span>
                                </div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <button onClick={() => toggleNotice(n.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '10px', color: n.is_active ? '#10b981' : '#94a3b8' }}>
                                        {n.is_active ? '✓ Active' : '○ Inactive'}
                                    </button>
                                    <button onClick={() => { setNoticeForm({ id: n.id, title: n.title || '', message: n.message, type: n.type, priority: n.priority }); setShowNoticeModal(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: '10px' }}>Edit</button>
                                    <button onClick={() => deleteNotice(n.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '10px' }}>Delete</button>
                                </div>
                            </div>
                        )) : <div style={{ color: '#94a3b8', fontSize: '11px', textAlign: 'center', padding: '20px' }}>No notices yet. Add one to share with everyone!</div>}
                    </div>
                </div>
            )}

            {/* Stats Row */}
            <div className="stats-grid compact">
                {viewMode === 'org' ? (
                    stats.map((stat, idx) => (
                        <div key={idx} className="stat-card-vertical" onClick={() => navigate(stat.path)}>
                            <div className="stat-card-inner">
                                <div className="stat-icon-circle" style={{ backgroundColor: stat.bg }}>
                                    <stat.icon size={20} color={stat.color} />
                                </div>
                                <div className="stat-data">
                                    <span className="stat-label-v">{stat.name}</span>
                                    <div className="stat-main-row">
                                        <span className="stat-value-v">{stat.value}</span>
                                        <span className={`stat-pill ${stat.changeType === 'increase' ? 'positive' : 'neutral'}`}>
                                            {stat.change}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <>
                        <div className="stat-card-vertical" onClick={() => navigate('/attendance')}>
                            <div className="stat-card-inner">
                                <div className="stat-icon-circle" style={{ backgroundColor: 'var(--green-subtle)' }}>
                                    <CheckCircle size={20} color="#10b981" />
                                </div>
                                <div className="stat-data">
                                    <span className="stat-label-v">My Status</span>
                                    <div className="stat-main-row">
                                        <span className="stat-value-v">Present</span>
                                        <span className="stat-pill positive">On Time</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="stat-card-vertical" onClick={() => navigate('/leaves')}>
                            <div className="stat-card-inner">
                                <div className="stat-icon-circle" style={{ backgroundColor: 'var(--blue-subtle)' }}>
                                    <FileText size={20} color="#3b82f6" />
                                </div>
                                <div className="stat-data">
                                    <span className="stat-label-v">Leave Balance</span>
                                    <div className="stat-main-row">
                                        <span className="stat-value-v">{personalStats.leaveBalance} Days</span>
                                        <span className="stat-pill neutral">Available</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="stat-card-vertical" onClick={() => navigate('/leaves')}>
                            <div className="stat-card-inner">
                                <div className="stat-icon-circle" style={{ backgroundColor: 'var(--orange-subtle)' }}>
                                    <Clock size={20} color="#f59e0b" />
                                </div>
                                <div className="stat-data">
                                    <span className="stat-label-v">Pending Req</span>
                                    <div className="stat-main-row">
                                        <span className="stat-value-v">{personalStats.pendingLeaves}</span>
                                        <span className="stat-pill neutral">In Review</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="stat-card-vertical" onClick={() => navigate('/holidays')}>
                            <div className="stat-card-inner">
                                <div className="stat-icon-circle" style={{ backgroundColor: 'var(--purple-subtle)' }}>
                                    <Calendar size={20} color="#8b5cf6" />
                                </div>
                                <div className="stat-data">
                                    <span className="stat-label-v">Next Holiday</span>
                                    <div className="stat-main-row">
                                        <span className="stat-value-v" style={{ fontSize: '14px' }}>{personalStats.upcomingHoliday}</span>
                                        <span className="stat-pill neutral">Public</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Visuals Section */}
            <div className="charts-grid">
                <div className="chart-card large premium">
                    <div className="card-header">
                        <div className="header-title">
                            <h3>{viewMode === 'org' ? 'Analytics Overview' : 'Weekly Activity'}</h3>
                            <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>{viewMode === 'org' ? 'Organization metrics visualization' : 'Your engagement hours'}</p>
                        </div>
                        {viewMode === 'org' && (
                            <div className="card-actions">
                                <button className={`chart-toggle-btn ${chartMode === 'growth' ? 'active' : ''}`} onClick={() => setChartMode('growth')}><TrendingUp size={14} /> Growth</button>
                                <button className={`chart-toggle-btn ${chartMode === 'dept' ? 'active' : ''}`} onClick={() => setChartMode('dept')}><BarChart2 size={14} /> Departments</button>
                            </div>
                        )}
                    </div>
                    <div className="chart-body">
                        {chartMode === 'growth' || viewMode === 'personal' ? <EmployeeGrowthChart data={growthData} /> : <DepartmentChart data={deptData} />}
                    </div>
                </div>

                <div className="chart-card small premium">
                    <div className="card-header">
                        <h3>{viewMode === 'org' ? 'Attendance Radar' : 'Monthly Breakdown'}</h3>
                    </div>
                    <div className="chart-body flex-center">
                        <AttendancePieChart data={attendanceData} />
                        <div className="legend-grid-compact">
                            {attendanceData.map((d, i) => (
                                <div key={i} className="legend-item-v">
                                    <span className="dot" style={{ backgroundColor: d.color }}></span>
                                    <span className="lbl">{d.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Daily Summary Section - Comprehensive */}
            {viewMode === 'org' && (
                <div className="daily-summary-section" style={{ marginTop: '24px' }}>
                    {/* Header with Date Picker */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1e293b' }}>
                            📊 Daily Summary - {new Date(dailySummaryDate).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' })}
                        </h2>
                        <input
                            type="date"
                            value={dailySummaryDate}
                            onChange={(e) => handleDailySummaryDateChange(e.target.value)}
                            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', cursor: 'pointer' }}
                        />
                    </div>

                    {/* Pending Approvals Banner */}
                    {(dailySummary.pendingApprovals?.leaves > 0 || dailySummary.pendingApprovals?.wfh > 0 || dailySummary.pendingApprovals?.regularization > 0) && (
                        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <AlertCircle size={20} color="#d97706" />
                            <span style={{ fontSize: '13px', color: '#92400e' }}>
                                <strong>Pending Approvals:</strong> {dailySummary.pendingApprovals?.leaves || 0} Leaves • {dailySummary.pendingApprovals?.wfh || 0} WFH • {dailySummary.pendingApprovals?.regularization || 0} Regularization
                            </span>
                            <button onClick={() => navigate('/leaves')} style={{ marginLeft: 'auto', background: '#d97706', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer' }}>
                                Review Now
                            </button>
                        </div>
                    )}

                    {/* Attendance Stats Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', marginBottom: '20px' }}>
                        <div style={{ background: '#dcfce7', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#16a34a' }}>{dailySummary.summary?.present || 0}</div>
                            <div style={{ fontSize: '11px', color: '#166534' }}>Present</div>
                        </div>
                        <div style={{ background: '#fee2e2', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>{dailySummary.summary?.absent || 0}</div>
                            <div style={{ fontSize: '11px', color: '#991b1b' }}>Absent</div>
                        </div>
                        <div style={{ background: '#fef3c7', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#d97706' }}>{dailySummary.summary?.late || 0}</div>
                            <div style={{ fontSize: '11px', color: '#92400e' }}>Late</div>
                        </div>
                        <div style={{ background: '#dbeafe', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2563eb' }}>{dailySummary.summary?.wfh || 0}</div>
                            <div style={{ fontSize: '11px', color: '#1e40af' }}>WFH</div>
                        </div>
                        <div style={{ background: '#f3e8ff', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#7c3aed' }}>{dailySummary.summary?.onLeave || 0}</div>
                            <div style={{ fontSize: '11px', color: '#5b21b6' }}>On Leave</div>
                        </div>
                        <div style={{ background: '#f1f5f9', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#475569' }}>{dailySummary.summary?.halfDay || 0}</div>
                            <div style={{ fontSize: '11px', color: '#64748b' }}>Half Day</div>
                        </div>
                    </div>

                    {/* Grid of Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '20px' }}>

                        {/* Leaves Today */}
                        <div className="info-card-premium" style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <FileText size={16} color="#7c3aed" />
                                <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>On Leave Today</h4>
                            </div>
                            <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                {dailySummary.leavesToday?.length > 0 ? dailySummary.leavesToday.map((l, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '11px' }}>
                                        <span style={{ fontWeight: '500' }}>{l.first_name} {l.last_name}</span>
                                        <span style={{ color: '#7c3aed', background: '#f3e8ff', padding: '2px 6px', borderRadius: '4px' }}>{l.leave_type}</span>
                                    </div>
                                )) : <div style={{ color: '#94a3b8', fontSize: '11px', textAlign: 'center', padding: '20px 0' }}>No one on leave</div>}
                            </div>
                        </div>

                        {/* WFH Today */}
                        <div className="info-card-premium" style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <Briefcase size={16} color="#2563eb" />
                                <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>Working From Home</h4>
                            </div>
                            <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                {dailySummary.wfhToday?.length > 0 ? dailySummary.wfhToday.map((w, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '11px' }}>
                                        <span style={{ fontWeight: '500' }}>{w.first_name} {w.last_name}</span>
                                        <span style={{
                                            color: w.status === 'APPROVED' ? '#16a34a' : w.status === 'PENDING' ? '#d97706' : '#dc2626',
                                            background: w.status === 'APPROVED' ? '#dcfce7' : w.status === 'PENDING' ? '#fef3c7' : '#fee2e2',
                                            padding: '2px 6px', borderRadius: '4px'
                                        }}>{w.status}</span>
                                    </div>
                                )) : <div style={{ color: '#94a3b8', fontSize: '11px', textAlign: 'center', padding: '20px 0' }}>No WFH requests</div>}
                            </div>
                        </div>

                        {/* Regularization Requests */}
                        <div className="info-card-premium" style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <Clock size={16} color="#d97706" />
                                <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>Regularization Requests</h4>
                            </div>
                            <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                {dailySummary.regularizationToday?.length > 0 ? dailySummary.regularizationToday.map((r, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '11px' }}>
                                        <span style={{ fontWeight: '500' }}>{r.first_name} {r.last_name}</span>
                                        <span style={{
                                            color: r.status === 'APPROVED' ? '#16a34a' : r.status === 'PENDING' ? '#d97706' : '#dc2626',
                                            background: r.status === 'APPROVED' ? '#dcfce7' : r.status === 'PENDING' ? '#fef3c7' : '#fee2e2',
                                            padding: '2px 6px', borderRadius: '4px'
                                        }}>{r.status}</span>
                                    </div>
                                )) : <div style={{ color: '#94a3b8', fontSize: '11px', textAlign: 'center', padding: '20px 0' }}>No requests</div>}
                            </div>
                        </div>

                        {/* Birthdays */}
                        <div className="info-card-premium" style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <span style={{ fontSize: '16px' }}>🎂</span>
                                <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>Birthdays Today</h4>
                            </div>
                            <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                {dailySummary.birthdays?.length > 0 ? dailySummary.birthdays.map((b, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '11px' }}>
                                        <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>🎉</div>
                                        <div>
                                            <div style={{ fontWeight: '500' }}>{b.first_name} {b.last_name}</div>
                                            <div style={{ color: '#64748b', fontSize: '10px' }}>{b.designation_name || 'Team Member'}</div>
                                        </div>
                                    </div>
                                )) : <div style={{ color: '#94a3b8', fontSize: '11px', textAlign: 'center', padding: '20px 0' }}>No birthdays today</div>}
                            </div>
                        </div>

                        {/* Work Anniversaries */}
                        <div className="info-card-premium" style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <span style={{ fontSize: '16px' }}>🏆</span>
                                <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>Work Anniversaries</h4>
                            </div>
                            <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                {dailySummary.anniversaries?.length > 0 ? dailySummary.anniversaries.map((a, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '11px' }}>
                                        <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>⭐</div>
                                        <div>
                                            <div style={{ fontWeight: '500' }}>{a.first_name} {a.last_name}</div>
                                            <div style={{ color: '#2563eb', fontSize: '10px' }}>{a.years} year{a.years > 1 ? 's' : ''} completed</div>
                                        </div>
                                    </div>
                                )) : <div style={{ color: '#94a3b8', fontSize: '11px', textAlign: 'center', padding: '20px 0' }}>No anniversaries today</div>}
                            </div>
                        </div>

                        {/* Upcoming Holidays */}
                        <div className="info-card-premium" style={{ padding: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <Calendar size={16} color="#10b981" />
                                <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>Upcoming Holidays</h4>
                            </div>
                            <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                                {dailySummary.upcomingHolidays?.length > 0 ? dailySummary.upcomingHolidays.map((h, i) => (
                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: '11px' }}>
                                        <span style={{ fontWeight: '500' }}>{h.name}</span>
                                        <span style={{ color: '#10b981', background: '#dcfce7', padding: '2px 6px', borderRadius: '4px' }}>{h.date_display}</span>
                                    </div>
                                )) : <div style={{ color: '#94a3b8', fontSize: '11px', textAlign: 'center', padding: '20px 0' }}>No upcoming holidays</div>}
                            </div>
                        </div>
                    </div>

                    {/* Attendance Table */}
                    <div className="info-card-premium" style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h4 style={{ margin: 0, fontSize: '13px', fontWeight: '600' }}>👥 Employee Attendance Details</h4>
                            <button onClick={() => navigate('/attendance')} style={{ background: 'none', border: 'none', color: '#3b82f6', fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                View All <ChevronRight size={12} />
                            </button>
                        </div>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: '600', color: '#64748b' }}>Employee</th>
                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600', color: '#64748b' }}>In</th>
                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600', color: '#64748b' }}>Out</th>
                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600', color: '#64748b' }}>Hours</th>
                                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: '600', color: '#64748b' }}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {dailySummary.employees?.length > 0 ? dailySummary.employees.map((emp, idx) => (
                                        <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '8px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <div style={{ width: '24px', height: '24px', borderRadius: '50%', background: '#e0e7ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 'bold', color: '#4f46e5' }}>
                                                        {(emp.first_name || 'U').charAt(0)}
                                                    </div>
                                                    <span style={{ fontWeight: '500' }}>{emp.first_name} {emp.last_name}</span>
                                                </div>
                                            </td>
                                            <td style={{ padding: '8px', textAlign: 'center', color: '#16a34a' }}>{emp.punch_in || '--:--'}</td>
                                            <td style={{ padding: '8px', textAlign: 'center', color: '#dc2626' }}>{emp.punch_out || '--:--'}</td>
                                            <td style={{ padding: '8px', textAlign: 'center' }}>{emp.duration_hrs ? `${emp.duration_hrs}h` : '-'}</td>
                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                <span style={{
                                                    padding: '3px 6px', borderRadius: '10px', fontSize: '9px', fontWeight: '600',
                                                    background: emp.status_name === 'Present' ? '#dcfce7' : emp.status_name === 'Absent' ? '#fee2e2' : '#fef3c7',
                                                    color: emp.status_name === 'Present' ? '#16a34a' : emp.status_name === 'Absent' ? '#dc2626' : '#d97706'
                                                }}>{emp.status_name}</span>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>No attendance data</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* Bottom Insights */}
            <div className="bottom-grid condensed">
                <div className="info-card-premium full-height">
                    <div className="card-header-v">
                        <Activity size={18} className="text-blue" />
                        <h3>{viewMode === 'org' ? 'Shortcuts' : 'Quick Actions'}</h3>
                    </div>
                    <div className="quick-actions-list-compact">
                        {(viewMode === 'org' ? [
                            { name: 'Workforce', icon: Users, color: 'blue', path: '/employees' },
                            { name: 'Approvals', icon: Clock, color: 'orange', path: '/leaves' },
                            { name: 'Payroll', icon: DollarSign, color: 'purple', path: '/payroll' },
                            { name: 'Pipeline', icon: UserCheck, color: 'emerald', path: '/onboarding' }
                        ] : [
                            { name: 'Profile', icon: Bookmark, color: 'blue', path: '/settings/profile' },
                            { name: 'Leave Req', icon: FileText, color: 'orange', path: '/leaves' },
                            { name: 'Payslip', icon: DollarSign, color: 'purple', path: '/payroll' },
                            { name: 'Exit process', icon: LogOut, color: 'red', path: '/offboarding-exit' }
                        ]).map((item, i) => (
                            <div key={i} className="qa-item-compact" onClick={() => navigate(item.path)}>
                                <item.icon size={18} />
                                <span>{item.name}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="info-card-premium full-height">
                    <div className="card-header-v">
                        <Bell size={18} className="text-orange" />
                        <h3>{viewMode === 'org' ? 'Live Feed' : 'My Requests'}</h3>
                    </div>
                    <div className="activity-list-v scrollable">
                        {(viewMode === 'org' ? recentLeaves : myRequests).length > 0 ? (viewMode === 'org' ? recentLeaves : myRequests).map(item => (
                            <div key={item.id} className="feed-row">
                                <div className="feed-avatar">{(viewMode === 'org' ? item.employee_name : (item.type || 'L')).charAt(0)}</div>
                                <div className="feed-info">
                                    <span className="feed-name">{viewMode === 'org' ? item.employee_name : `Request #${item.id.toString().slice(-4)}`}</span>
                                    <span className="feed-desc">{item.type} • {item.days || item.total_days}d • {item.status}</span>
                                </div>
                            </div>
                        )) : <div className="no-data-text-v">No recent actions.</div>}
                    </div>
                </div>

                <div className="info-card-premium full-height">
                    <div className="card-header-v">
                        <Star size={18} className="text-emerald" />
                        <h3>{viewMode === 'org' ? 'Recent Joinees' : 'Announcements'}</h3>
                    </div>
                    <div className="activity-list-v scrollable">
                        {viewMode === 'org' ? (
                            onboardingList.length > 0 ? onboardingList.map(emp => (
                                <div key={emp.id} className="feed-row">
                                    <div className="feed-avatar blue">{(emp.first_name || 'U').charAt(0)}</div>
                                    <div className="feed-info">
                                        <span className="feed-name">{emp.first_name} {emp.last_name}</span>
                                        <span className="feed-desc">{emp.designation_name || 'Team'} • {emp.status_name}</span>
                                    </div>
                                    <div className={emp.status_name?.toUpperCase() === 'EXITED' ? '' : 'active-pulse'}></div>
                                </div>
                            )) : <div className="no-data-text-v">{refreshing ? 'Loading joiners...' : 'No recent entries.'}</div>
                        ) : (
                            <div className="no-data-text-v" style={{ textAlign: 'center', padding: '20px' }}>
                                <Briefcase size={32} color="#e2e8f0" style={{ marginBottom: '10px' }} />
                                <p style={{ fontSize: '11px' }}>Company wide announcements will appear here.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Bottom Section (Conditional) */}
            {viewMode === 'org' && (
                <div className="bottom-grid condensed" style={{ marginTop: '24px' }}>
                    <div className="info-card-premium" style={{ gridColumn: 'span 2' }}>
                        <div className="card-header-v">
                            <BarChart2 size={18} className="text-purple" />
                            <h3>Department Master (External System)</h3>
                        </div>
                        <div className="quick-actions-list-compact" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                            {externalDepts.length > 0 ? externalDepts.map(d => (
                                <div key={d.departmentId} className="qa-item-compact" style={{ padding: '10px' }}>
                                    <span style={{ fontSize: '11px', fontWeight: 'bold' }}>{d.departmentName}</span>
                                    <span style={{ fontSize: '9px', color: '#64748b' }}>ID: {d.departmentId}</span>
                                </div>
                            )) : <div className="no-data-text-v">{refreshing ? 'Loading departments...' : 'No departments found.'}</div>}
                        </div>
                    </div>

                    <div className="info-card-premium">
                        <div className="card-header-v">
                            <Zap size={18} className="text-orange" />
                            <h3>Detailed Stats</h3>
                        </div>
                        <div className="activity-list-v">
                            <div className="feed-row" style={{ justifyContent: 'space-between', padding: '6px 4px' }}>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>Retention Rate</span>
                                <span style={{ fontWeight: 'bold', color: '#10b981', fontSize: '14px' }}>{detailedStats.retention}%</span>
                            </div>
                            <div className="feed-row" style={{ justifyContent: 'space-between', padding: '6px 4px' }}>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>Diversity Index</span>
                                <span style={{ fontWeight: 'bold', color: '#3b82f6', fontSize: '14px' }}>{detailedStats.diversity}</span>
                            </div>
                            <div className="feed-row" style={{ justifyContent: 'space-between', padding: '6px 4px' }}>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>Avg Tenure</span>
                                <span style={{ fontWeight: 'bold', color: '#f59e0b', fontSize: '14px' }}>{detailedStats.tenure} Yrs</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Notice Modal - Premium */}
            {showNoticeModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                    animation: 'fadeIn 0.2s ease-out'
                }}>
                    <div style={{
                        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                        borderRadius: '20px',
                        padding: '0',
                        width: '480px',
                        maxWidth: '90%',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255,255,255,0.1)',
                        overflow: 'hidden',
                        animation: 'slideUp 0.3s ease-out'
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            padding: '20px 24px',
                            position: 'relative',
                            overflow: 'hidden'
                        }}>
                            <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '100px', height: '100px', background: 'rgba(255,255,255,0.1)', borderRadius: '50%' }}></div>
                            <div style={{ position: 'absolute', bottom: '-30px', left: '20%', width: '60px', height: '60px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }}></div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative', zIndex: 1 }}>
                                <div style={{
                                    width: '44px', height: '44px', borderRadius: '12px',
                                    background: 'rgba(255,255,255,0.2)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    backdropFilter: 'blur(10px)'
                                }}>
                                    <Bell size={22} color="white" />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'white' }}>
                                        {noticeForm.id ? '✏️ Edit Notice' : '📢 Create New Notice'}
                                    </h3>
                                    <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
                                        Share important updates with your team
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '24px' }}>
                            <div style={{ marginBottom: '18px' }}>
                                <label style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#334155'
                                }}>
                                    <span style={{ fontSize: '14px' }}>📝</span> Title
                                    <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '400' }}>(Optional)</span>
                                </label>
                                <input
                                    type="text"
                                    value={noticeForm.title}
                                    onChange={(e) => setNoticeForm({ ...noticeForm, title: e.target.value })}
                                    placeholder="e.g., Important Update, Team Meeting, Policy Change..."
                                    style={{
                                        width: '100%', padding: '12px 16px',
                                        border: '2px solid #e2e8f0', borderRadius: '10px',
                                        fontSize: '14px', transition: 'all 0.2s ease',
                                        outline: 'none', background: '#f8fafc'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                                />
                            </div>

                            <div style={{ marginBottom: '18px' }}>
                                <label style={{
                                    display: 'flex', alignItems: 'center', gap: '6px',
                                    fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#334155'
                                }}>
                                    <span style={{ fontSize: '14px' }}>💬</span> Message
                                    <span style={{ color: '#ef4444' }}>*</span>
                                </label>
                                <textarea
                                    value={noticeForm.message}
                                    onChange={(e) => setNoticeForm({ ...noticeForm, message: e.target.value })}
                                    placeholder="Write your notice or announcement here... This will be visible to all employees on their dashboard."
                                    rows={4}
                                    style={{
                                        width: '100%', padding: '12px 16px',
                                        border: '2px solid #e2e8f0', borderRadius: '10px',
                                        fontSize: '14px', resize: 'vertical', minHeight: '100px',
                                        outline: 'none', background: '#f8fafc', lineHeight: '1.5'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                                <div>
                                    <label style={{
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                        fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#334155'
                                    }}>
                                        <span style={{ fontSize: '14px' }}>🎨</span> Type
                                    </label>
                                    <select
                                        value={noticeForm.type}
                                        onChange={(e) => setNoticeForm({ ...noticeForm, type: e.target.value })}
                                        style={{
                                            width: '100%', padding: '12px 16px',
                                            border: '2px solid #e2e8f0', borderRadius: '10px',
                                            fontSize: '14px', cursor: 'pointer',
                                            outline: 'none', background: '#f8fafc',
                                            appearance: 'none',
                                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2364748b' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E")`,
                                            backgroundRepeat: 'no-repeat',
                                            backgroundPosition: 'right 12px center'
                                        }}
                                    >
                                        <option value="info">ℹ️ Info (Blue)</option>
                                        <option value="success">✅ Success (Green)</option>
                                        <option value="warning">⚠️ Warning (Yellow)</option>
                                        <option value="urgent">🚨 Urgent (Red)</option>
                                    </select>
                                </div>
                                <div>
                                    <label style={{
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                        fontSize: '13px', fontWeight: '600', marginBottom: '8px', color: '#334155'
                                    }}>
                                        <span style={{ fontSize: '14px' }}>⚡</span> Priority
                                    </label>
                                    <select
                                        value={noticeForm.priority}
                                        onChange={(e) => setNoticeForm({ ...noticeForm, priority: parseInt(e.target.value) })}
                                        style={{
                                            width: '100%', padding: '12px 16px',
                                            border: '2px solid #e2e8f0', borderRadius: '10px',
                                            fontSize: '14px', cursor: 'pointer',
                                            outline: 'none', background: '#f8fafc',
                                            appearance: 'none',
                                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='%2364748b' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E")`,
                                            backgroundRepeat: 'no-repeat',
                                            backgroundPosition: 'right 12px center'
                                        }}
                                    >
                                        <option value={0}>🔵 Normal</option>
                                        <option value={1}>🟠 High</option>
                                        <option value={2}>🔴 Very High</option>
                                    </select>
                                </div>
                            </div>

                            {/* Preview Badge */}
                            <div style={{
                                background: '#f1f5f9', borderRadius: '10px', padding: '12px 16px',
                                marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px'
                            }}>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>Preview:</span>
                                <span style={{
                                    padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                                    background: noticeForm.type === 'urgent' ? '#fee2e2' : noticeForm.type === 'warning' ? '#fef3c7' : noticeForm.type === 'success' ? '#dcfce7' : '#dbeafe',
                                    color: noticeForm.type === 'urgent' ? '#dc2626' : noticeForm.type === 'warning' ? '#d97706' : noticeForm.type === 'success' ? '#16a34a' : '#2563eb'
                                }}>
                                    {noticeForm.type === 'urgent' ? '🚨 URGENT' : noticeForm.type === 'warning' ? '⚠️ WARNING' : noticeForm.type === 'success' ? '✅ SUCCESS' : 'ℹ️ INFO'}
                                </span>
                                <span style={{
                                    padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600',
                                    background: noticeForm.priority === 2 ? '#fef2f2' : noticeForm.priority === 1 ? '#fff7ed' : '#f0f9ff',
                                    color: noticeForm.priority === 2 ? '#b91c1c' : noticeForm.priority === 1 ? '#c2410c' : '#0369a1'
                                }}>
                                    {noticeForm.priority === 2 ? '🔴 Very High' : noticeForm.priority === 1 ? '🟠 High' : '🔵 Normal'}
                                </span>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div style={{
                            padding: '16px 24px',
                            background: '#f8fafc',
                            borderTop: '1px solid #e2e8f0',
                            display: 'flex', gap: '12px', justifyContent: 'flex-end'
                        }}>
                            <button
                                onClick={() => setShowNoticeModal(false)}
                                style={{
                                    padding: '12px 24px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '10px',
                                    background: 'white',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#64748b',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => { e.target.style.borderColor = '#cbd5e1'; e.target.style.background = '#f8fafc'; }}
                                onMouseOut={(e) => { e.target.style.borderColor = '#e2e8f0'; e.target.style.background = 'white'; }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={saveNotice}
                                disabled={!noticeForm.message.trim()}
                                style={{
                                    padding: '12px 28px',
                                    border: 'none',
                                    borderRadius: '10px',
                                    background: noticeForm.message.trim() ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#cbd5e1',
                                    color: 'white',
                                    cursor: noticeForm.message.trim() ? 'pointer' : 'not-allowed',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    boxShadow: noticeForm.message.trim() ? '0 4px 14px rgba(102, 126, 234, 0.4)' : 'none',
                                    transition: 'all 0.2s ease',
                                    display: 'flex', alignItems: 'center', gap: '8px'
                                }}
                                onMouseOver={(e) => { if (noticeForm.message.trim()) e.target.style.transform = 'translateY(-1px)'; }}
                                onMouseOut={(e) => { e.target.style.transform = 'translateY(0)'; }}
                            >
                                {noticeForm.id ? '💾 Update Notice' : '🚀 Publish Notice'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;

